import {
  collection, collectionGroup, doc, getDoc, getDocs, updateDoc, setDoc,
  onSnapshot, query, where, serverTimestamp, arrayUnion, arrayRemove, writeBatch, runTransaction
} from "firebase/firestore";
import { onIdTokenChanged } from "firebase/auth";
import { db, auth } from "./config";
import {
  createOwnerResources, getOwnerEventCodes, getRedeemedDisplayCodes,
  invalidateOwnerEventCodes, redeemAccessCode, sendAccessCommand,
} from "./accessSessions";
import { DEFAULT_DISCIPLINE_VISIBILITY } from "../utils/scoring";
import { withoutPoolRef } from "../utils/poolAssignments";
import { confirmedRoundIndexes } from "../utils/judgeSubmission";

// ─── CODE REGISTRY ───
// Top-level "codes" collection for fast O(1) lookups instead of scanning all events

const boutCodeFields = { bout: "code", boutOfficial: "officialCode", boutRef: "refCode", boutSpectate: "spectateCode" };
const poolCodeFields = { pool: "code", poolSpectate: "spectateCode" };

const hydrateBoutCodes = async (eventId, bout) => {
  if (!bout) return bout;
  const codes = { ...getRedeemedDisplayCodes(eventId, bout.id) };
  (await getOwnerEventCodes(eventId)).filter((entry) => entry.boutId === bout.id)
    .forEach((entry) => { codes[entry.type] = entry.code; });
  const hydrated = { ...bout };
  Object.entries(boutCodeFields).forEach(([type, field]) => { if (codes[type]) hydrated[field] = codes[type]; });
  return hydrated;
};

const hydratePoolCodes = async (eventId, disciplineId, pool) => {
  if (!pool) return pool;
  const codes = { ...getRedeemedDisplayCodes(eventId, `${disciplineId}:${pool.name}`) };
  (await getOwnerEventCodes(eventId)).filter((entry) => entry.disciplineId === disciplineId && entry.poolName === pool.name)
    .forEach((entry) => { codes[entry.type] = entry.code; });
  const hydrated = { ...pool };
  Object.entries(poolCodeFields).forEach(([type, field]) => { if (codes[type]) hydrated[field] = codes[type]; });
  return hydrated;
};

export const lookupCode = async (code) => {
  return redeemAccessCode(code.toUpperCase());
};

// ─── USER PROFILES ───
// users/{uid} holds profile fields that live outside Firebase Auth (displayName stays
// on the Auth user itself, updated via updateProfile in useAuth.js).

export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : { affiliation: "", bio: "" };
};

export const updateUserProfile = async (uid, { affiliation, bio }) => {
  await setDoc(doc(db, "users", uid), {
    affiliation: affiliation || "",
    bio: bio || "",
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// ─── EVENTS ───

export const createEvent = async ({ name, type, disciplines }) =>
  createOwnerResources("createEvent", { name, type, disciplines });

export const getEvent = async (eventId) => {
  const snap = await getDoc(doc(db, "events", eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getEventByCode = async (code) => {
  const lookup = await lookupCode(code);
  if (lookup?.type === "event" && lookup.eventId) {
    const snap = await getDoc(doc(db, "events", lookup.eventId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  }
  return null;
};

export const subscribeToEvent = (eventId, callback) => {
  let publicEvent = null;
  let privateEvent = null;
  let privateLoaded = false;
  let unsubscribePrivate = null;
  let currentUid = auth.currentUser?.uid || null;
  let privateSubscriptionVersion = 0;

  const emit = () => {
    if (!publicEvent) callback(null);
    else if (privateLoaded) callback({ ...publicEvent, ...(privateEvent || {}) });
  };

  const configurePrivateSubscription = () => {
    const isOwner = publicEvent?.createdBy && publicEvent.createdBy === currentUid;
    if (isOwner && !unsubscribePrivate) {
      privateLoaded = false;
      privateEvent = null;
      const version = ++privateSubscriptionVersion;
      unsubscribePrivate = onSnapshot(
        doc(db, "events", eventId, "private", "roster"),
        (privateSnap) => {
          if (version !== privateSubscriptionVersion) return;
          privateEvent = privateSnap.exists() ? privateSnap.data() : {};
          privateLoaded = true;
          emit();
        },
        (error) => {
          if (version !== privateSubscriptionVersion) return;
          console.error("subscribeToEvent private data failed:", error);
          privateEvent = {};
          privateLoaded = true;
          emit();
        }
      );
    } else if (!isOwner) {
      privateSubscriptionVersion++;
      if (unsubscribePrivate) unsubscribePrivate();
      unsubscribePrivate = null;
      privateEvent = null;
      privateLoaded = true;
      emit();
    } else {
      emit();
    }
  };

  const unsubscribePublic = onSnapshot(doc(db, "events", eventId), (snap) => {
    publicEvent = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    configurePrivateSubscription();
  });

  const unsubscribeAuth = onIdTokenChanged(auth, (user) => {
    currentUid = user?.uid || null;
    configurePrivateSubscription();
  });

  return () => {
    unsubscribePublic();
    unsubscribeAuth();
    privateSubscriptionVersion++;
    if (unsubscribePrivate) unsubscribePrivate();
  };
};

const privateEventRef = (eventId) => doc(db, "events", eventId, "private", "roster");
const updatePrivateEvent = async (eventId, data) => {
  await setDoc(privateEventRef(eventId), data, { merge: true });
};

export const migratePrivateEventData = async (eventId) => {
  // This read succeeds only after the owner-only private rules are deployed.
  // Never move legacy fields if the current client could not read the result.
  await getDoc(privateEventRef(eventId));
  if (!auth.currentUser) throw new Error("Authentication required");
  const response = await fetch("/api/migratePrivateEventData", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
    },
    body: JSON.stringify({ eventId }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Unable to migrate private event data");
  return payload;
};

export const subscribeToUserEvents = (uid, callback) => {
  const q = query(collection(db, "events"), where("createdBy", "==", uid));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const setEventPublicVisibility = async (eventId, isPublic) => {
  await updateDoc(doc(db, "events", eventId), { isPublic });
};

// Firestore never cascade-deletes subcollections when a parent doc is deleted, so an
// event's disciplines and bouts (and every code - event, pool, pool-spectate, bout,
// bout-ref, bout-spectate - registered against them in the top-level `codes` collection)
// have to be cleaned up by hand or they'd dangle, pointing at documents that no longer
// exist. Deletes are chunked to stay under Firestore's 500-write batch limit.
export const deleteEvent = async (eventId) => {
  const eventRef = doc(db, "events", eventId);

  const [disciplinesSnap, boutsSnap, historySnap, codesSnap] = await Promise.all([
    getDocs(collection(db, "events", eventId, "disciplines")),
    getDocs(collection(db, "events", eventId, "bouts")),
    getDocs(collection(db, "events", eventId, "boutHistory")),
    getDocs(query(collection(db, "codes"), where("eventId", "==", eventId))),
  ]);
  const poolSnaps = await Promise.all(disciplinesSnap.docs.map((disciplineDoc) =>
    getDocs(collection(db, "events", eventId, "disciplines", disciplineDoc.id, "pools"))
  ));

  const refs = [
    ...poolSnaps.flatMap((snap) => snap.docs.map((d) => d.ref)),
    ...boutsSnap.docs.map((d) => d.ref),
    ...historySnap.docs.map((d) => d.ref),
    ...codesSnap.docs.map((d) => d.ref),
    ...disciplinesSnap.docs.map((d) => d.ref),
    privateEventRef(eventId),
    eventRef,
  ];
  for (let i = 0; i < refs.length; i += 450) {
    const batch = writeBatch(db);
    refs.slice(i, i + 450).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
  invalidateOwnerEventCodes(eventId);
};

export const getEventsByIds = async (eventIds) => {
  const uniqueIds = [...new Set(eventIds)];
  const results = await Promise.all(uniqueIds.map(async (id) => {
    const snap = await getDoc(doc(db, "events", id));
    return snap.exists() ? { id, ...snap.data() } : null;
  }));
  return results.filter(Boolean);
};

// Bouts across all events where the logged-in user judged at least one round.
// Private history pointers identify the user's judge name without exposing a UID
// on the public bout document.
const historyEntryId = (uid, boutId) => `${uid}--${boutId}`;
const historyEntryRef = (eventId, uid, boutId) =>
  doc(db, "events", eventId, "boutHistory", historyEntryId(uid, boutId));

const addBoutHistory = (eventId, boutId, uid, field, name) => {
  if (!uid) return Promise.resolve();
  return setDoc(historyEntryRef(eventId, uid, boutId), {
    uid, eventId, boutId, [field]: arrayUnion(name), updatedAt: serverTimestamp(),
  }, { merge: true });
};

const subscribeToUserBoutHistory = (uid, field, callback) => {
  const entries = new Map();
  const bouts = new Map();
  const boutUnsubscribes = new Map();
  let loaded = false;
  const emit = () => {
    if (loaded && bouts.size >= boutUnsubscribes.size) callback([...bouts.values()].filter(Boolean));
  };
  const unsubscribeIndex = onSnapshot(
    query(collectionGroup(db, "boutHistory"), where("uid", "==", uid)),
    (snap) => {
      const nextEntries = new Map(snap.docs
        .map((d) => d.data())
        .filter((entry) => (entry[field] || []).length > 0)
        .map((entry) => [`${entry.eventId}/${entry.boutId}`, entry]));
      for (const [key, unsubscribe] of boutUnsubscribes) {
        if (!nextEntries.has(key)) {
          unsubscribe();
          boutUnsubscribes.delete(key);
          bouts.delete(key);
          entries.delete(key);
        }
      }
      for (const [key, entry] of nextEntries) {
        entries.set(key, entry);
        if (bouts.get(key)) bouts.set(key, { ...bouts.get(key), _history: entry });
        if (boutUnsubscribes.has(key)) continue;
        const unsubscribe = onSnapshot(
          doc(db, "events", entry.eventId, "bouts", entry.boutId),
          (boutSnap) => {
            bouts.set(key, boutSnap.exists()
              ? { id: boutSnap.id, eventId: entry.eventId, ...boutSnap.data(), _history: entries.get(key) }
              : null);
            emit();
          },
          (err) => {
            console.error("subscribeToUserBoutHistory bout failed:", err);
            bouts.set(key, null);
            emit();
          }
        );
        boutUnsubscribes.set(key, unsubscribe);
      }
      loaded = true;
      emit();
    },
    (err) => {
      console.error("subscribeToUserBoutHistory failed:", err);
      loaded = true;
      callback([]);
    }
  );
  return () => {
    unsubscribeIndex();
    boutUnsubscribes.forEach((unsubscribe) => unsubscribe());
  };
};

export const subscribeToUserJudgedBouts = (uid, callback) =>
  subscribeToUserBoutHistory(uid, "judgeNames", callback);

// Bouts across all events where the logged-in user is a linked fighter. The same
// private index carries the fighter name or names represented by the account.
export const subscribeToUserFighterBoutHistory = (uid, callback) => {
  return subscribeToUserBoutHistory(uid, "fighterNames", callback);
};

export const setSweepsVisibility = async (eventId, sweepsVisible) => {
  await updateDoc(doc(db, "events", eventId), { sweepsVisible });
};

export const updateEventDetails = async (eventId, { name, startDate, endDate, location }) => {
  await updateDoc(doc(db, "events", eventId), { name, startDate, endDate, location });
};

// Free-form welcome message shown on the public event page's HOME tab —
// admin composes it (welcome text, parking info, schedules, etc.) as plain text.
export const updateEventWelcomeContent = async (eventId, welcomeContent) => {
  await updateDoc(doc(db, "events", eventId), { welcomeContent });
};

// Per-discipline schedule (tournament mode) — { [disciplineId]: { eventDate, checkInTime,
// poolStartTime } }, "YYYY-MM-DD"/"HH:MM" strings straight from <input type="date">/
// <input type="time"> (check-in and pool start share one date per discipline). Edited on
// the SETTINGS tab's Schedule section, shown on the public event page's SCHEDULE tab.
// Whole-map setter (mirrors updateEventFighterDisciplines etc.) — the caller merges the
// one changed discipline's field into the existing map before calling.
export const updateEventDisciplineSchedules = async (eventId, disciplineSchedules) => {
  await updateDoc(doc(db, "events", eventId), { disciplineSchedules });
};

// Powers the "Browse Events" list on the landing page — events an admin has
// opted into showing publicly, so spectators can find them without a code.
export const subscribeToPublicEvents = (callback) => {
  const q = query(collection(db, "events"), where("isPublic", "==", true));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

// seminar/training participants
export const addParticipants = async (eventId, names) => {
  const trimmed = names.map((n) => n.trim()).filter(Boolean);
  await updateDoc(doc(db, "events", eventId), { participants: arrayUnion(...trimmed) });
};

export const removeParticipant = async (eventId, name, currentList) => {
  await updateDoc(doc(db, "events", eventId), { participants: currentList.filter((p) => p !== name) });
};

// Event-level judges & refs roster (tournament mode). Any judge/ref on this
// list can work any discipline in the event — there's no per-discipline
// assignment, this is the one roster everything reads from.
export const addEventOfficials = async (eventId, names) => {
  const trimmed = names.map((n) => n.trim()).filter(Boolean);
  await updateDoc(doc(db, "events", eventId), { officials: arrayUnion(...trimmed) });
};

export const removeEventOfficial = async (eventId, name, currentList) => {
  await updateDoc(doc(db, "events", eventId), { officials: currentList.filter((p) => p !== name) });
};

export const updateEventOfficialAffiliations = async (eventId, affiliations) => {
  await updateDoc(doc(db, "events", eventId), { officialAffiliations: affiliations });
};

export const updateEventOfficialEmails = async (eventId, emails) => {
  await updatePrivateEvent(eventId, { officialEmails: emails });
};

// Per-official role ("referee" | "judge") — referees can be assigned as either a
// bout's ref or a judge; plain judges can only be assigned as a judge. Missing
// entries (officials added before this field existed) are treated as "judge".
export const updateEventOfficialRoles = async (eventId, roles) => {
  await updateDoc(doc(db, "events", eventId), { officialRoles: roles });
};

// Event-level fighter roster (tournament mode), mirroring the officials roster
// above. A fighter's identity/affiliation/email/account is the same across every
// discipline they enter, so this is the source of truth — disc.fighters[] stays
// a plain per-discipline "who's entered here" array on top of this. `fighterUids`
// (name -> uid) is written only by api/claimFighter.js via the Admin SDK, never
// from the client, so there's no updateEventFighterUids here.
export const addEventFighters = async (eventId, names) => {
  const trimmed = names.map((n) => n.trim()).filter(Boolean);
  await updateDoc(doc(db, "events", eventId), { fighters: arrayUnion(...trimmed) });
};

// Also strips the fighter from every discipline's `fighters[]` (and their
// fighterDisciplines entry) in the same batch — with discipline membership now
// only editable from this event-level roster, a full removal here needs to take
// disc.fighters with it or the name would keep showing up on discipline pages
// with no way to remove it there.
export const removeEventFighter = async (eventId, name, currentList, disciplines = [], fighterDisciplines = {}) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "events", eventId), {
    fighters: currentList.filter((p) => p !== name),
    ...(fighterDisciplines[name] !== undefined
      ? { fighterDisciplines: Object.fromEntries(Object.entries(fighterDisciplines).filter(([n]) => n !== name)) }
      : {}),
  });
  disciplines.forEach((disc) => {
    if ((disc.fighters || []).includes(name)) {
      batch.update(doc(db, "events", eventId, "disciplines", disc.id), {
        fighters: disc.fighters.filter((f) => f !== name),
      });
    }
  });
  await batch.commit();
};

export const updateEventFighterAffiliations = async (eventId, affiliations) => {
  await updateDoc(doc(db, "events", eventId), { fighterAffiliations: affiliations });
};

// A pending claim (see api/claimFighter.js) already has its uid's ownership of the
// shared email verified server-side - approving here just means the admin agrees
// all of `names` really do belong to one account (e.g. a parent's kids), so it's
// safe as a direct client write from the admin's own session, same trust level as
// the rest of this file's event-doc writes. Also marks the shared email itself as
// dismissed (see updateEventFighterSharedEmailDismissed) so the plain "same email"
// heads-up on the FIGHTERS tab doesn't keep showing once the admin has already
// approved linking it to one account.
export const approveFighterClaim = async (
  eventId, uid, names, email, fighterUids = {}, pendingFighterClaims = {}, fighterSharedEmailDismissed = {}
) => {
  const nextPending = { ...pendingFighterClaims };
  delete nextPending[uid];
  const batch = writeBatch(db);
  batch.set(privateEventRef(eventId), {
    fighterUids: { ...fighterUids, ...Object.fromEntries(names.map((n) => [n, uid])) },
    pendingFighterClaims: nextPending,
    fighterSharedEmailDismissed: { ...fighterSharedEmailDismissed, [email.toLowerCase()]: true },
  }, { merge: true });

  const boutsRef = collection(db, "events", eventId, "bouts");
  const namesByBout = new Map();
  for (const name of names) {
    const [blueSnap, redSnap] = await Promise.all([
      getDocs(query(boutsRef, where("fighterBlue", "==", name))),
      getDocs(query(boutsRef, where("fighterRed", "==", name))),
    ]);
    [...blueSnap.docs, ...redSnap.docs].forEach((d) => {
      if (!namesByBout.has(d.id)) namesByBout.set(d.id, []);
      namesByBout.get(d.id).push(name);
    });
  }
  for (const [boutId, fighterNames] of namesByBout) {
    batch.set(historyEntryRef(eventId, uid, boutId), {
      uid, eventId, boutId,
      fighterNames: arrayUnion(...fighterNames), updatedAt: serverTimestamp(),
    }, { merge: true });
  }
  await batch.commit();
};

// Silences the plain "same email as X" heads-up (no claim attempt yet, just two
// roster names sharing an email) without requiring anyone to actually sign in
// first - keyed by the email itself, not the names, since the warning is about
// the email pairing and should stay silenced even if a name gets edited slightly.
// Re-arms automatically if the email is later changed to something new that
// happens to collide with a different name (a fresh email value isn't in this
// map yet).
export const updateEventFighterSharedEmailDismissed = async (eventId, dismissed) => {
  await updatePrivateEvent(eventId, { fighterSharedEmailDismissed: dismissed });
};

// Editing a fighter's email is how the admin says "this pairing was wrong" (e.g. the
// email really was a paste mistake, not one person's two kids) - a pending claim's
// matched names are only valid while they all still share the exact email that
// produced them, so changing any one of those names' emails invalidates the whole
// claim rather than leaving a stale approve prompt pointing at outdated names.
export const clearFighterFromPendingClaims = async (eventId, name, pendingFighterClaims = {}) => {
  const affected = Object.entries(pendingFighterClaims).filter(([, claim]) => claim.names.includes(name));
  if (affected.length === 0) return;
  const nextPending = { ...pendingFighterClaims };
  for (const [uid] of affected) delete nextPending[uid];
  await updatePrivateEvent(eventId, { pendingFighterClaims: nextPending });
};

// Waivers are signed once per tournament, not once per discipline — a fighter entered
// in both Longsword and Rapier only signs one waiver, so this lives on the event doc
// (like affiliations) rather than disc.fighterWaivers (which check-in status still uses,
// since checking in genuinely happens separately at each discipline's ring).
export const updateEventFighterWaivers = async (eventId, waivers) => {
  await updatePrivateEvent(eventId, { fighterWaivers: waivers });
};

export const updateEventFighterEmails = async (eventId, emails) => {
  await updatePrivateEvent(eventId, { fighterEmails: emails });
};

// { [fighterName]: displayName } — the registered account's display name the admin has
// confirmed is just a spelling/nickname variant of this fighter, not a mix-up (FightersPanel's
// "This is them" button on the name-mismatch warning). Keyed by the display name at the time
// of dismissal rather than a plain boolean flag, so if the email later gets repointed at a
// *different* registered account the warning re-arms automatically instead of staying silenced.
export const updateEventFighterMismatchDismissed = async (eventId, dismissed) => {
  await updatePrivateEvent(eventId, { fighterMismatchDismissed: dismissed });
};

export const updateEventFighterDisciplines = async (eventId, fighterDisciplines) => {
  await updateDoc(doc(db, "events", eventId), { fighterDisciplines });
};

// Which disciplines a fighter is registered for (event-level, tournament mode) —
// { [name]: [disciplineId, ...] }. This is the single place admins edit fighter/
// discipline membership; `disc.fighters[]` on each discipline doc stays the field
// pools/standings/sweepstakes/bracket seeding actually read, so every write here
// also syncs it (arrayUnion/filter) on whichever disciplines were added or removed
// for this fighter, in the same batch as the event-level map update.
export const setFighterDisciplines = async (eventId, name, disciplineIds, disciplines, fighterDisciplines) => {
  const batch = writeBatch(db);
  batch.update(doc(db, "events", eventId), {
    fighterDisciplines: { ...fighterDisciplines, [name]: disciplineIds },
  });
  disciplines.forEach((disc) => {
    const shouldBeIn = disciplineIds.includes(disc.id);
    const isIn = (disc.fighters || []).includes(name);
    if (shouldBeIn && !isIn) {
      batch.update(doc(db, "events", eventId, "disciplines", disc.id), { fighters: arrayUnion(name) });
    } else if (!shouldBeIn && isIn) {
      batch.update(doc(db, "events", eventId, "disciplines", disc.id), {
        fighters: disc.fighters.filter((f) => f !== name),
      });
    }
  });
  await batch.commit();
};

// Bulk-imports a roster file from the FIGHTERS tab (utils/rosterImport.js's
// resolveRosterDisciplines output: { [disciplineName]: string[] fighters }, plus the
// raw parsed summary for affiliations/emails/judges) — creates any discipline named
// in the file that doesn't already exist (pre-populating its fighters[] directly
// rather than a separate write), unions every fighter into the matching
// discipline(s), and updates the event doc's fighters/fighterDisciplines/
// fighterEmails/fighterAffiliations/disciplines to match. One batch so a partial
// failure can't leave a new discipline with no roster or a fighter missing their
// discipline registration. fighterDisciplines is append-only here (same as the
// migration effects in EventDashboard.js) — it never drops a discipline a fighter
// was already registered for outside this file. The file's school column also
// carries each fighter's affiliation, and an optional judge list is folded into the
// event's officials roster (the same fields JudgesPanel manages:
// officials[]/officialAffiliations{}/officialRoles{}) rather than the fighter
// roster — a person who is both a fighter and a judge in the file is added to both
// rosters independently.
export const importRosterData = async (eventId, {
  rosters,
  fighterEmails = {},
  fighterAffiliations = {},
  judgeNames = [],
  existingDisciplines,
  existingFighterDisciplines = {},
  existingFighterEmails = {},
  existingFighterAffiliations = {},
  existingOfficialAffiliations = {},
  existingOfficialRoles = {},
}) => {
  const batch = writeBatch(db);
  const disciplineIdByName = {};
  const newDisciplineNames = [];

  existingDisciplines.forEach((disc) => { disciplineIdByName[disc.name] = disc.id; });

  for (const [name, names] of Object.entries(rosters)) {
    if (disciplineIdByName[name] || !names.length) continue;
    const ref = doc(collection(db, "events", eventId, "disciplines"));
    disciplineIdByName[name] = ref.id;
    newDisciplineNames.push(name);
    batch.set(ref, {
      name,
      fighters: names,
      poolCount: 0,
      phase: "pool",
      roundsPerBout: 1,
      visibility: DEFAULT_DISCIPLINE_VISIBILITY,
      createdAt: serverTimestamp(),
    });
  }

  existingDisciplines.forEach((disc) => {
    const names = rosters[disc.name];
    if (!names?.length) return;
    const toAdd = names.filter((n) => !(disc.fighters || []).includes(n));
    if (toAdd.length) batch.update(doc(db, "events", eventId, "disciplines", disc.id), { fighters: arrayUnion(...toAdd) });
  });

  const allFighterNames = [...new Set(Object.values(rosters).flat())];
  const eventUpdate = {};
  const privateUpdate = {};

  if (allFighterNames.length) {
    const nextFighterDisciplines = { ...existingFighterDisciplines };
    for (const [discName, names] of Object.entries(rosters)) {
      const discId = disciplineIdByName[discName];
      for (const name of names) {
        const current = nextFighterDisciplines[name] || [];
        if (!current.includes(discId)) nextFighterDisciplines[name] = [...current, discId];
      }
    }
    eventUpdate.fighters = arrayUnion(...allFighterNames);
    eventUpdate.fighterDisciplines = nextFighterDisciplines;
    if (newDisciplineNames.length) eventUpdate.disciplines = arrayUnion(...newDisciplineNames);

    const scopedEmails = Object.fromEntries(
      Object.entries(fighterEmails).filter(([name]) => allFighterNames.includes(name))
    );
    if (Object.keys(scopedEmails).length) {
      privateUpdate.fighterEmails = { ...existingFighterEmails, ...scopedEmails };
    }
    const scopedAffiliations = Object.fromEntries(
      Object.entries(fighterAffiliations).filter(([name]) => allFighterNames.includes(name))
    );
    if (Object.keys(scopedAffiliations).length) {
      eventUpdate.fighterAffiliations = { ...existingFighterAffiliations, ...scopedAffiliations };
    }
  }

  if (judgeNames.length) {
    eventUpdate.officials = arrayUnion(...judgeNames);
    const nextOfficialAffiliations = { ...existingOfficialAffiliations };
    const nextOfficialRoles = { ...existingOfficialRoles };
    judgeNames.forEach((name) => {
      const aff = fighterAffiliations[name];
      if (aff) nextOfficialAffiliations[name] = aff;
      if (!nextOfficialRoles[name]) nextOfficialRoles[name] = "judge";
    });
    eventUpdate.officialAffiliations = nextOfficialAffiliations;
    eventUpdate.officialRoles = nextOfficialRoles;
  }

  if (Object.keys(eventUpdate).length) {
    batch.update(doc(db, "events", eventId), eventUpdate);
  }
  if (Object.keys(privateUpdate).length) {
    batch.set(privateEventRef(eventId), privateUpdate, { merge: true });
  }

  await batch.commit();
};

// Content for the fighter welcome email (api/sendFighterWelcomeEmail.js), authored
// separately from welcomeContent above — that field is written for the public HOME
// page's audience/context (spectators browsing alongside other tabs), which doesn't
// translate cleanly into a standalone email.
export const updateEventFighterWelcomeEmail = async (eventId, { subject, bodyHtml, headerImage, headerImageAlt = "" }) => {
  await updateDoc(doc(db, "events", eventId), {
    fighterWelcomeEmailSubject: subject,
    fighterWelcomeEmail: bodyHtml,
    fighterWelcomeEmailHeaderImage: headerImage || "",
    fighterWelcomeEmailHeaderImageAlt: headerImageAlt,
  });
};

// Content for the general-purpose fighter update email (api/sendFighterCustomEmail.js) —
// a second template alongside the welcome email, for schedule changes, pool postings, or
// other one-off announcements the admin sends out as needed over the course of an event.
export const updateEventFighterCustomEmail = async (eventId, { subject, bodyHtml, headerImage, headerImageAlt = "" }) => {
  await updateDoc(doc(db, "events", eventId), {
    fighterCustomEmailSubject: subject,
    fighterCustomEmailBody: bodyHtml,
    fighterCustomEmailHeaderImage: headerImage || "",
    fighterCustomEmailHeaderImageAlt: headerImageAlt,
  });
};

const MAX_RICH_CONTENT_IMAGE_BYTES = 4 * 1024 * 1024;

// Images dropped into a RichTextEditor (event welcome message, fighter welcome email
// body) need a public URL - email clients and the public event page can't load an
// authenticated Firestore/Storage read, so these go to a world-readable Storage path
// (see storage.rules) rather than Firestore itself. storage.rules blocks direct
// client writes entirely now, so this goes through api/uploadRichContentImage.js
// (Admin SDK), which checks the caller is both an approved admin and this event's
// creator before writing - see that file's header comment for why this moved
// server-side instead of living in storage.rules itself.
export const uploadRichContentImage = async (eventId, file) => {
  if (!file.type.startsWith("image/")) throw new Error("File must be an image");
  if (file.size > MAX_RICH_CONTENT_IMAGE_BYTES) throw new Error("Image must be 4MB or smaller");
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch(`/api/uploadRichContentImage?eventId=${encodeURIComponent(eventId)}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": file.type,
      "X-Filename": file.name,
    },
    body: file,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to upload image");
  return data.url;
};

// ─── ADMIN REQUESTS ───
// "Only admins can create events" gate - see AdminAccessGate.js, Account.js's
// ADMIN REQUESTS tab, and firestore.rules' adminRequests/{uid} rule. Admin status
// itself is a Firebase Auth custom claim (see useAuth.js's isAdmin), granted only
// via api/approveAdminRequest.js - this collection just tracks the request/approval
// workflow that leads up to that.

export const requestAdminAccess = async (uid, email, { name, affiliation, reason }) => {
  await setDoc(doc(db, "adminRequests", uid), {
    uid,
    email: email || "",
    name: name || "",
    affiliation: affiliation || "",
    reason: reason || "",
    status: "pending",
    requestedAt: serverTimestamp(),
  });
};

export const subscribeToOwnAdminRequest = (uid, callback) => {
  return onSnapshot(doc(db, "adminRequests", uid), (snap) => {
    callback(snap.exists() ? snap.data() : null);
  });
};

export const subscribeToPendingAdminRequests = (callback) => {
  const q = query(collection(db, "adminRequests"), where("status", "==", "pending"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => d.data()));
  });
};

export const respondToAdminRequest = async (targetUid, action) => {
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch("/api/approveAdminRequest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken, targetUid, action }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to respond to admin request");
  return data;
};

// ─── DISCIPLINES (tournament mode) ───
// Collection: events/{eventId}/disciplines/{disciplineId}

export const createDiscipline = async (eventId, name) => {
  const eventRef = doc(db, "events", eventId);
  const disciplineRef = doc(collection(eventRef, "disciplines"));
  const batch = writeBatch(db);
  batch.set(disciplineRef, {
    name,
    fighters: [],
    poolCount: 0, // pools live in the pools/ subcollection - this is just a denormalized count for list views
    phase: "pool",
    roundsPerBout: 1,
    visibility: DEFAULT_DISCIPLINE_VISIBILITY,
    createdAt: serverTimestamp(),
  });
  // Keep the event doc's denormalized name list (shown on Account.js event cards) in sync
  batch.update(eventRef, { disciplines: arrayUnion(name) });
  await batch.commit();
  return disciplineRef.id;
};

// Merges the discipline doc with its pools/ subcollection so callers keep
// seeing the same shape as when pools lived in a `pools` array field on the
// discipline doc itself - only this function and the pool-mutating functions
// below know pools are stored separately.
export const subscribeToDiscipline = (eventId, disciplineId, callback) => {
  let discData;
  let poolsData;
  let discLoaded = false;
  let poolsLoaded = false;
  let emissionVersion = 0;

  const emit = async () => {
    const version = ++emissionVersion;
    if (!discLoaded || !poolsLoaded) return;
    if (!discData) { callback(null); return; }
    const hydratedPools = await Promise.all(poolsData.map((pool) => hydratePoolCodes(eventId, disciplineId, pool)));
    if (version !== emissionVersion) return;
    callback({ ...discData, pools: hydratedPools });
  };

  const unsub1 = onSnapshot(doc(db, "events", eventId, "disciplines", disciplineId), (snap) => {
    discData = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    discLoaded = true;
    emit();
  });
  const unsub2 = onSnapshot(collection(db, "events", eventId, "disciplines", disciplineId, "pools"), (snap) => {
    poolsData = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    poolsLoaded = true;
    emit();
  });

  return () => { unsub1(); unsub2(); };
};

export const subscribeToDisciplines = (eventId, callback) => {
  return onSnapshot(collection(db, "events", eventId, "disciplines"), (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const updateDiscipline = async (eventId, disciplineId, data) => {
  await updateDoc(doc(db, "events", eventId, "disciplines", disciplineId), data);
};

export const removeDisciplineFighter = async (eventId, disciplineId, name, currentList) => {
  await updateDoc(doc(db, "events", eventId, "disciplines", disciplineId), {
    fighters: currentList.filter((p) => p !== name),
  });
};

// ─── POOLS ───

// `pools` is always the COMPLETE desired set for the discipline (the editor
// UI always passes the full list, not a delta) - diff it against what's
// currently in the pools/ subcollection and batch create/update/delete.
export const savePools = async (eventId, disciplineId, pools, refName) =>
  createOwnerResources("savePools", { eventId, disciplineId, pools, refName });


// Look up a pool by its shared code
export const getPoolByCode = async (code) => {
  const lookup = await lookupCode(code);
  if (lookup?.type === "pool" && lookup.eventId && lookup.disciplineId && lookup.poolName) {
    const eventSnap = await getDoc(doc(db, "events", lookup.eventId));
    const discSnap = await getDoc(doc(db, "events", lookup.eventId, "disciplines", lookup.disciplineId));
    if (eventSnap.exists() && discSnap.exists()) {
      const poolsRef = collection(db, "events", lookup.eventId, "disciplines", lookup.disciplineId, "pools");
      const q = query(poolsRef, where("name", "==", lookup.poolName));
      const poolsSnap = await getDocs(q);
      const poolDoc = poolsSnap.docs[0];
      if (poolDoc) {
        return {
          eventId: lookup.eventId,
          eventName: eventSnap.data().name,
          disciplineId: lookup.disciplineId,
          disciplineName: discSnap.data().name,
          pool: await hydratePoolCodes(lookup.eventId, lookup.disciplineId, { id: poolDoc.id, ...poolDoc.data() }),
        };
      }
    }
  }
  return null;
};

// Look up a pool by its spectator code
export const getPoolBySpectateCode = async (code) => {
  const lookup = await lookupCode(code);
  if (lookup?.type === "poolSpectate" && lookup.eventId && lookup.disciplineId && lookup.poolName) {
    const eventSnap = await getDoc(doc(db, "events", lookup.eventId));
    const discSnap = await getDoc(doc(db, "events", lookup.eventId, "disciplines", lookup.disciplineId));
    if (eventSnap.exists() && discSnap.exists()) {
      const poolsRef = collection(db, "events", lookup.eventId, "disciplines", lookup.disciplineId, "pools");
      const q = query(poolsRef, where("name", "==", lookup.poolName));
      const poolsSnap = await getDocs(q);
      const poolDoc = poolsSnap.docs[0];
      if (poolDoc) {
        return {
          eventId: lookup.eventId,
          eventName: eventSnap.data().name,
          disciplineId: lookup.disciplineId,
          disciplineName: discSnap.data().name,
          pool: await hydratePoolCodes(lookup.eventId, lookup.disciplineId, { id: poolDoc.id, ...poolDoc.data() }),
        };
      }
    }
  }
  return null;
};

// ─── ROUND-ROBIN BOUT GENERATION ───

const generateRoundRobin = (fighters) => {
  const bouts = [];
  for (let i = 0; i < fighters.length; i++) {
    for (let j = i + 1; j < fighters.length; j++) {
      bouts.push({ fighterBlue: fighters[i], fighterRed: fighters[j], pairingIndex: bouts.length });
    }
  }
  return bouts;
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Randomize bout order while avoiding back-to-back bouts for the same fighter where possible
const scheduleBouts = (pairings) => {
  const remaining = shuffle(pairings);
  const scheduled = [];
  while (remaining.length) {
    const prev = scheduled[scheduled.length - 1];
    let idx = remaining.findIndex(p => !prev ||
      (p.fighterBlue !== prev.fighterBlue && p.fighterBlue !== prev.fighterRed &&
       p.fighterRed !== prev.fighterBlue && p.fighterRed !== prev.fighterRed));
    if (idx === -1) idx = 0; // every remaining pairing shares a fighter with prev — take the next one anyway
    scheduled.push(remaining.splice(idx, 1)[0]);
  }
  return scheduled;
};

export const generatePoolBouts = async (eventId, disciplineId, discipline, fighterUids = {}) => {
  const pools = discipline.pools || [];
  const phase = discipline.phase || "pool";
  const roundsPerBout = discipline.roundsPerBout || 1;
  const existingSnap = await getDocs(query(
    collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId)
  ));
  const pairingKey = (poolName, poolRound, fighterBlue, fighterRed) => JSON.stringify([
    poolName,
    poolRound,
    ...[fighterBlue, fighterRed].sort(),
  ]);
  const existingPairings = new Set(existingSnap.docs
    .map((d) => d.data())
    .filter((bout) => bout.eliminationRound == null)
    .map((bout) => pairingKey(bout.poolName, bout.poolRound || 1, bout.fighterBlue, bout.fighterRed)));

  for (const pool of pools) {
    if (!pool.id) throw new Error(`Pool ${pool.name} is missing its persisted ID`);
    const pairings = scheduleBouts(generateRoundRobin(pool.fighters));
    for (let i = 0; i < pairings.length; i++) {
      const pairing = pairings[i];
      const poolRound = pool.round || 1;
      const key = pairingKey(pool.name, poolRound, pairing.fighterBlue, pairing.fighterRed);
      if (existingPairings.has(key)) continue;
      const boutRef = doc(db, "events", eventId, "bouts", `pool-${pool.id}-r${poolRound}-m${pairing.pairingIndex}`);
      const batch = writeBatch(db);
      batch.set(boutRef, {
        fighterBlue: pairing.fighterBlue,
        fighterRed: pairing.fighterRed,
        judges: pool.judges || [],
        phase,
        totalRounds: roundsPerBout,
        order: i,
        status: "waiting",
        currentRound: 0,
        confirmedRounds: [],
        activeJudges: [],
        judgeSubmissions: {},
        confirmedResult: null,
        disciplineId,
        disciplineName: discipline.name,
        poolName: pool.name,
        poolId: pool.id,
        poolRound,
        refName: pool.ref || "",
        time: "",
        location: "",
        createdAt: serverTimestamp(),
      });
      const namesByUid = new Map();
      for (const fighterName of [pairing.fighterBlue, pairing.fighterRed]) {
        const uid = fighterUids[fighterName];
        if (!uid) continue;
        if (!namesByUid.has(uid)) namesByUid.set(uid, []);
        namesByUid.get(uid).push(fighterName);
      }
      for (const [uid, fighterNames] of namesByUid) {
        batch.set(historyEntryRef(eventId, uid, boutRef.id), {
          uid, eventId, boutId: boutRef.id,
          fighterNames: arrayUnion(...fighterNames), updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      await batch.commit();
      existingPairings.add(key);
    }
  }
};

const deleteBoutDocumentsAndCodes = async (eventId, boutDocs) => {
  const boutIds = new Set(boutDocs.map((boutDoc) => boutDoc.id));
  const [codesSnap, historySnap] = await Promise.all([
    getDocs(query(collection(db, "codes"), where("eventId", "==", eventId))),
    getDocs(collection(db, "events", eventId, "boutHistory")),
  ]);
  const refs = codesSnap.docs.filter((codeDoc) => boutIds.has(codeDoc.data().boutId)).map((codeDoc) => codeDoc.ref);
  historySnap.docs.filter((historyDoc) => boutIds.has(historyDoc.data().boutId))
    .forEach((historyDoc) => refs.push(historyDoc.ref));
  boutDocs.forEach((boutDoc) => {
    refs.push(boutDoc.ref);
  });
  for (let i = 0; i < refs.length; i += 450) {
    const batch = writeBatch(db);
    refs.slice(i, i + 450).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
  invalidateOwnerEventCodes(eventId);
};

export const deleteDisciplineBouts = async (eventId, disciplineId) => {
  const q = query(collection(db, "events", eventId, "bouts"), where("disciplineId", "==", disciplineId));
  const snap = await getDocs(q);
  await deleteBoutDocumentsAndCodes(eventId, snap.docs);
};

export const resetPools = async (eventId, disciplineId) => {
  const poolsRef = collection(db, "events", eventId, "disciplines", disciplineId, "pools");
  const [poolsSnap, codesSnap] = await Promise.all([
    getDocs(poolsRef),
    getDocs(query(collection(db, "codes"), where("eventId", "==", eventId))),
  ]);
  const batch = writeBatch(db);
  poolsSnap.docs.forEach((poolDoc) => batch.delete(poolDoc.ref));
  codesSnap.docs.filter((codeDoc) => codeDoc.data().disciplineId === disciplineId && ["pool", "poolSpectate"].includes(codeDoc.data().type))
    .forEach((codeDoc) => batch.delete(codeDoc.ref));
  batch.update(doc(db, "events", eventId, "disciplines", disciplineId), {
    pools: [],
    poolCount: 0,
  });
  await batch.commit();
  invalidateOwnerEventCodes(eventId);
};

// ─── ELIMINATION ───

export const deleteEliminationBouts = async (eventId, disciplineId) => {
  const q = query(collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId)
  );
  const snap = await getDocs(q);
  await deleteBoutDocumentsAndCodes(eventId, snap.docs.filter((boutDoc) => boutDoc.data().eliminationRound != null));
};

export const resetElimination = async (eventId, disciplineId) => {
  await deleteEliminationBouts(eventId, disciplineId);
  await updateDoc(doc(db, "events", eventId, "disciplines", disciplineId), {
    elimination: null,
  });
};

export const generateEliminationBouts = async (
  eventId, disciplineId, disciplineName, matchups, stageConfig, bracketSize
) => createOwnerResources("createElimination", {
  eventId, disciplineId, disciplineName, matchups, stageConfig, bracketSize,
});


export const createNextRoundBout = async (
  eventId, disciplineId, disciplineName, round, matchIndex, fighter1, fighter2, roundsPerBout, phase
) => createOwnerResources("createNextRoundBout", {
  eventId, disciplineId, disciplineName, round, matchIndex, fighter1, fighter2, roundsPerBout, phase,
});


// ─── BOUTS ───

export const createBout = async (eventId, boutData) =>
  createOwnerResources("createBout", { eventId, ...boutData });


export const getBoutByCode = async (code) => {
  const lookup = await lookupCode(code);
  if (["bout", "boutOfficial"].includes(lookup?.type) && lookup.eventId && lookup.boutId) {
    const boutSnap = await getDoc(doc(db, "events", lookup.eventId, "bouts", lookup.boutId));
    if (boutSnap.exists()) {
      const eventSnap = await getDoc(doc(db, "events", lookup.eventId));
      return hydrateBoutCodes(lookup.eventId, {
        id: boutSnap.id,
        eventId: lookup.eventId,
        eventName: eventSnap.data()?.name || "",
        accessType: lookup.type,
        matchedOfficialNames: lookup.matchedOfficialNames || [],
        ...boutSnap.data(),
      });
    }
  }
  return null;
};

export const getBoutByRefCode = async (code) => {
  const lookup = await lookupCode(code);
  if (lookup?.type === "boutRef" && lookup.eventId && lookup.boutId) {
    const boutSnap = await getDoc(doc(db, "events", lookup.eventId, "bouts", lookup.boutId));
    if (boutSnap.exists()) {
      const eventSnap = await getDoc(doc(db, "events", lookup.eventId));
      return hydrateBoutCodes(lookup.eventId, { id: boutSnap.id, eventId: lookup.eventId, eventName: eventSnap.data()?.name || "", ...boutSnap.data() });
    }
  }
  return null;
};

export const getBoutBySpectateCode = async (code) => {
  const lookup = await lookupCode(code);
  if (lookup?.type === "boutSpectate" && lookup.eventId && lookup.boutId) {
    const boutSnap = await getDoc(doc(db, "events", lookup.eventId, "bouts", lookup.boutId));
    if (boutSnap.exists()) {
      const eventSnap = await getDoc(doc(db, "events", lookup.eventId));
      return hydrateBoutCodes(lookup.eventId, { id: boutSnap.id, eventId: lookup.eventId, eventName: eventSnap.data()?.name || "", ...boutSnap.data() });
    }
  }
  return null;
};

export const subscribeToBout = (eventId, boutId, callback) => {
  return onSnapshot(doc(db, "events", eventId, "bouts", boutId), async (snap) => {
    callback(snap.exists() ? await hydrateBoutCodes(eventId, { id: snap.id, ...snap.data() }) : null);
  });
};

export const subscribeToEventBouts = (eventId, callback) => {
  return onSnapshot(collection(db, "events", eventId, "bouts"), async (snap) => {
    callback(await Promise.all(snap.docs.map((d) => hydrateBoutCodes(eventId, { id: d.id, ...d.data() }))));
  });
};

export const updateBout = async (eventId, boutId, data) => {
  const commandResult = await sendAccessCommand("updateBout", { eventId, boutId, data });
  if (commandResult !== null) return;
  await updateDoc(doc(db, "events", eventId, "bouts", boutId), data);
};

// Every pool/{poolId} lookup below goes through this - pool names are unique
// within a discipline (see the round-2 pool-naming note in CLAUDE.md), so a
// single query always resolves to the one pool doc that matters.
const getPoolDocByName = async (eventId, disciplineId, poolName) => {
  const poolsRef = collection(db, "events", eventId, "disciplines", disciplineId, "pools");
  const snap = await getDocs(query(poolsRef, where("name", "==", poolName)));
  return snap.docs[0] || null;
};

export const assignPoolJudges = async (eventId, disciplineId, poolName, judges) => {
  const poolDoc = await getPoolDocByName(eventId, disciplineId, poolName);
  if (!poolDoc) throw new Error("Pool not found");
  const pool = poolDoc.data();
  const nextJudges = withoutPoolRef(judges, pool.ref);
  const q = query(
    collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId),
    where("poolName", "==", poolName)
  );
  const snap = await getDocs(q);
  const claimedJudges = (pool.claimedJudges || []).filter(j => nextJudges.includes(j));
  if (snap.size >= 500) throw new Error("This pool has too many bouts to update safely in one operation");
  const batch = writeBatch(db);
  snap.docs.forEach(boutDoc => batch.update(boutDoc.ref, { judges: nextJudges }));
  // Keep the pool's judge roster (used by the pool join screen) in the same commit.
  batch.update(poolDoc.ref, { judges: nextJudges, claimedJudges });
  await batch.commit();
};

export const assignPoolRef = async (eventId, disciplineId, poolName, refName) => {
  const poolDoc = await getPoolDocByName(eventId, disciplineId, poolName);
  if (!poolDoc) throw new Error("Pool not found");
  const pool = poolDoc.data();
  const judges = withoutPoolRef(pool.judges, refName);
  const claimedJudges = (pool.claimedJudges || []).filter(name => judges.includes(name));
  const boutsSnap = await getDocs(query(
    collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId),
    where("poolName", "==", poolName)
  ));
  if (boutsSnap.size >= 500) throw new Error("This pool has too many bouts to update safely in one operation");
  const batch = writeBatch(db);
  batch.update(poolDoc.ref, { ref: refName, refClaimed: false, judges, claimedJudges });
  boutsSnap.docs.forEach(boutDoc => {
    const boutJudges = withoutPoolRef(boutDoc.data().judges, refName);
    batch.update(boutDoc.ref, { judges: boutJudges });
  });
  await batch.commit();
};

export const assignPoolRing = async (eventId, disciplineId, poolName, location) => {
  const poolDocPromise = getPoolDocByName(eventId, disciplineId, poolName);
  const q = query(
    collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId),
    where("poolName", "==", poolName)
  );
  const [poolDoc, boutsSnap] = await Promise.all([poolDocPromise, getDocs(q)]);
  if (!poolDoc) throw new Error("Pool not found");
  if (boutsSnap.size >= 500) throw new Error("This pool has too many bouts to update safely in one operation");

  const batch = writeBatch(db);
  boutsSnap.docs.forEach((boutDoc) => batch.update(boutDoc.ref, { location }));
  batch.update(poolDoc.ref, { location });
  await batch.commit();
};

// Advance a pool to the next unconfirmed bout, in round-robin order.
// Judges already connected to the pool pick this up automatically.
export const startNextPoolBout = async (eventId, disciplineId, poolName) => {
  const commandResult = await sendAccessCommand("startNextPoolBout", { eventId, disciplineId, poolName });
  if (commandResult !== null) return commandResult.nextBoutId;
  const poolDoc = await getPoolDocByName(eventId, disciplineId, poolName);
  if (!poolDoc) return;

  const q = query(
    collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId),
    where("poolName", "==", poolName)
  );
  const boutsSnap = await getDocs(q);
  const bouts = boutsSnap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const next = bouts.find(b => b.status !== "confirmed");
  if (!next) return null;

  await updateDoc(poolDoc.ref, { activeBoutId: next.id });
  return next.id;
};

// Finalize a completed pool and clear its active bout. Spectator lineups use the
// absence of an active bout to show every row, including the last result, as final.
export const confirmPoolResults = async (eventId, disciplineId, poolName) => {
  const commandResult = await sendAccessCommand("confirmPoolResults", { eventId, disciplineId, poolName });
  if (commandResult !== null) return;
  const poolDoc = await getPoolDocByName(eventId, disciplineId, poolName);
  if (!poolDoc) throw new Error("Pool not found");
  const boutsSnap = await getDocs(query(
    collection(db, "events", eventId, "bouts"),
    where("disciplineId", "==", disciplineId),
    where("poolName", "==", poolName)
  ));
  if (boutsSnap.empty || boutsSnap.docs.some((boutDoc) => boutDoc.data().status !== "confirmed")) {
    throw new Error("Every bout must be confirmed before confirming the pool");
  }
  await updateDoc(poolDoc.ref, {
    status: "confirmed",
    activeBoutId: null,
    confirmedAt: new Date().toISOString(),
  });
};

// Claim a ref or judge seat in a pool so the name is greyed out for anyone else using the same pool code
export const claimPoolSeat = async (eventId, disciplineId, poolName, role, judgeName = null) => {
  const commandResult = await sendAccessCommand("claimPoolSeat", { eventId, disciplineId, poolName, role, judgeName });
  if (commandResult !== null) return;
  const poolDoc = await getPoolDocByName(eventId, disciplineId, poolName);
  if (!poolDoc) return;
  if (role !== "ref" && role !== "judge") throw new Error("Invalid pool role");
  if (role === "judge" && !judgeName) throw new Error("Judge name is required");

  await runTransaction(db, async (transaction) => {
    const freshPool = await transaction.get(poolDoc.ref);
    if (!freshPool.exists()) throw new Error("Pool no longer exists");
    const pool = freshPool.data();
    if (role === "ref") {
      if (pool.refClaimed) throw new Error("Referee seat is already claimed");
      transaction.update(poolDoc.ref, { refClaimed: true });
      return;
    }
    if ((pool.claimedJudges || []).includes(judgeName)) throw new Error("Judge seat is already claimed");
    transaction.update(poolDoc.ref, { claimedJudges: arrayUnion(judgeName) });
  });
};

export const claimBoutRefSeat = async (eventId, boutId) => {
  const commandResult = await sendAccessCommand("claimBoutRefSeat", { eventId, boutId });
  if (commandResult === null) throw new Error("A shared bout access code is required");
};

// Release a claimed pool seat so it can be picked again (e.g. rejoining from a different device).
// For judges, this also forces out any device currently active in the pool's live bout — otherwise
// unlocking here only freed the name up on the join screen while an existing session kept scoring.
export const releasePoolSeat = async (eventId, disciplineId, poolName, role, judgeName = null) => {
  const commandResult = await sendAccessCommand("releasePoolSeat", { eventId, disciplineId, poolName, role, judgeName });
  if (commandResult !== null) return;
  const poolDoc = await getPoolDocByName(eventId, disciplineId, poolName);
  if (!poolDoc) return;
  const pool = poolDoc.data();
  if (role === "ref") {
    await updateDoc(poolDoc.ref, { refClaimed: false });
  } else {
    await updateDoc(poolDoc.ref, { claimedJudges: arrayRemove(judgeName) });
  }
  if (role === "judge" && pool.activeBoutId) {
    await unlockJudgeSeat(eventId, pool.activeBoutId, judgeName);
  }
};

// ─── JUDGE ───

export const claimJudgeSeat = async (eventId, boutId, judgeName) => {
  const commandResult = await sendAccessCommand("claimJudgeSeat", { eventId, boutId, judgeName });
  if (commandResult !== null) return;
  const updates = { activeJudges: arrayUnion(judgeName) };
  await updateDoc(doc(db, "events", eventId, "bouts", boutId), updates);
};

export const releaseJudgeSeat = async (eventId, boutId, judgeName) => {
  const commandResult = await sendAccessCommand("releaseJudgeSeat", { eventId, boutId, judgeName });
  if (commandResult !== null) return;
  const boutRef = doc(db, "events", eventId, "bouts", boutId);
  const boutSnap = await getDoc(boutRef);
  if (boutSnap.exists()) {
    const bout = boutSnap.data();
    const resetData = {
      activeJudges: arrayRemove(judgeName),
      judgeSubmissions: {},
      confirmedRounds: [],
      confirmedResult: null,
      currentRound: 0,
      status: "waiting",
    };
    // Reset totalRounds if tiebreaker had increased it
    if (bout.originalTotalRounds) {
      resetData.totalRounds = bout.originalTotalRounds;
      resetData.originalTotalRounds = null;
    }
    await updateDoc(boutRef, resetData);
  }
};

// Unlock a judge's seat without clearing their scores — lets them rejoin
export const unlockJudgeSeat = async (eventId, boutId, judgeName) => {
  const commandResult = await sendAccessCommand("unlockJudgeSeat", { eventId, boutId, judgeName });
  if (commandResult !== null) return;
  const boutRef = doc(db, "events", eventId, "bouts", boutId);
  const boutSnap = await getDoc(boutRef);
  if (boutSnap.exists()) {
    await updateDoc(boutRef, { activeJudges: arrayRemove(judgeName) });
  }
};

export const submitJudgeRound = async (eventId, boutId, judgeName, roundIndex, roundData, judgeUid = null) => {
  const commandResult = await sendAccessCommand("submitJudgeRound", { eventId, boutId, judgeName, roundIndex, roundData });
  if (commandResult !== null) return;
  const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
  const updates = {
    [`judgeSubmissions.${key}.rounds`]: arrayUnion({ index: roundIndex, ...roundData }),
    [`judgeSubmissions.${key}.judgeName`]: judgeName,
    [`judgeSubmissions.${key}.lastUpdated`]: new Date().toISOString(),
    [`liveTaps.${key}`]: { blue: 0, red: 0 },
    status: "scoring",
  };
  await updateDoc(doc(db, "events", eventId, "bouts", boutId), updates);
  await addBoutHistory(eventId, boutId, judgeUid, "judgeNames", judgeName);
};

// Live (unsubmitted) touch counts for a judge's current round — powers the spectator view
export const updateLiveTaps = async (eventId, boutId, judgeName, blue, red) => {
  const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
  try {
    const commandResult = await sendAccessCommand("updateLiveTaps", { eventId, boutId, judgeName, blue, red });
    if (commandResult !== null) return;
    await updateDoc(doc(db, "events", eventId, "bouts", boutId), {
      [`liveTaps.${key}`]: { blue, red },
    });
  } catch (err) { console.error("Failed to sync live taps:", err); }
};

// Judge corrects a round they already sent, while the ref hasn't confirmed it yet.
// Server-validated through /api/accessCommand; the direct write is the owner fallback.
export const editJudgeRound = async (eventId, boutId, judgeName, roundIndex, roundData) => {
  const commandResult = await sendAccessCommand("editJudgeRound", { eventId, boutId, judgeName, roundIndex, roundData });
  if (commandResult !== null) return;
  const boutRef = doc(db, "events", eventId, "bouts", boutId);
  const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(boutRef);
    if (!snap.exists()) throw new Error("Bout not found");
    const bout = snap.data();
    if (bout.status === "confirmed") throw new Error("This bout is already confirmed");
    const submission = bout.judgeSubmissions?.[key];
    if (submission?.complete) throw new Error("This card was already submitted; ask the referee to reopen it");
    if (confirmedRoundIndexes(bout.confirmedRounds).has(roundIndex)) throw new Error("The referee already confirmed that round");
    const rounds = submission?.rounds || [];
    if (!rounds.some((round) => round.index === roundIndex)) throw new Error("That round has not been submitted yet");
    tx.update(boutRef, {
      [`judgeSubmissions.${key}.rounds`]: rounds.map((round) => (round.index === roundIndex ? { index: roundIndex, ...roundData } : round)),
      [`judgeSubmissions.${key}.lastUpdated`]: new Date().toISOString(),
    });
  });
};

// Ref sends one judge's submitted final card back for correction. Other judges' scores and
// confirmed rounds are untouched; the reopen is logged on the bout as cardReopens[].
export const reopenJudgeCard = async (eventId, boutId, judgeName) => {
  const commandResult = await sendAccessCommand("reopenJudgeCard", { eventId, boutId, judgeName });
  if (commandResult !== null) return;
  const boutRef = doc(db, "events", eventId, "bouts", boutId);
  const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(boutRef);
    if (!snap.exists()) throw new Error("Bout not found");
    const bout = snap.data();
    if (bout.status === "confirmed") throw new Error("This bout is already confirmed");
    const submission = bout.judgeSubmissions?.[key];
    if (!submission?.complete) throw new Error("That judge has not submitted a final card");
    const reopenedAt = new Date().toISOString();
    tx.update(boutRef, {
      [`judgeSubmissions.${key}.complete`]: false,
      [`judgeSubmissions.${key}.reopenedAt`]: reopenedAt,
      cardReopens: arrayUnion({
        judgeName,
        reopenedAt,
        reopenedBy: bout.refName || "Referee",
        previousTotalBlue: submission.totalBlue ?? null,
        previousTotalRed: submission.totalRed ?? null,
      }),
      status: "scoring",
    });
  });
};

export const submitJudgeFinal = async (eventId, boutId, judgeName, scoreData, judgeUid = null) => {
  const commandResult = await sendAccessCommand("submitJudgeFinal", { eventId, boutId, judgeName, scoreData });
  if (commandResult !== null) return;
  const boutRef = doc(db, "events", eventId, "bouts", boutId);
  const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
  await updateDoc(boutRef, {
    [`judgeSubmissions.${key}`]: {
      judgeName,
      rounds: scoreData.rounds,
      totalBlue: scoreData.totalBlue,
      totalRed: scoreData.totalRed,
      boutWinner: scoreData.boutWinner,
      confirmedDraw: scoreData.confirmedDraw === true,
      boutWinnerComment: scoreData.boutWinnerComment || null,
      winner: scoreData.winner,
      submittedAt: new Date().toISOString(),
      complete: true,
    },
  });
  await addBoutHistory(eventId, boutId, judgeUid, "judgeNames", judgeName);
  const boutSnap = await getDoc(boutRef);
  if (boutSnap.exists()) {
    const bout = boutSnap.data();
    const subs = bout.judgeSubmissions || {};
    const allDone = (bout.judges || []).length > 0 && bout.judges.every((j) => {
      const k = j.replace(/[\.\#\$\/\[\]]/g, "_");
      return subs[k]?.complete;
    });
    if (allDone) await updateDoc(boutRef, { status: "submitted" });
  }
};

// ─── REF ───

// Locks in a round's score. Judges stay gated on `currentRound` until the ref
// explicitly opens the next round with startNextRound — confirming alone
// doesn't advance them.
export const confirmRound = async (eventId, boutId, roundIndex, roundScore) => {
  const commandResult = await sendAccessCommand("confirmRound", { eventId, boutId, roundIndex, roundScore });
  if (commandResult !== null) return;
  await updateDoc(doc(db, "events", eventId, "bouts", boutId), {
    confirmedRounds: arrayUnion({ roundIndex, blue: roundScore.blue, red: roundScore.red }),
    status: "scoring",
  });
};

// Ref-triggered: opens the next round for judges to start scoring.
export const startNextRound = async (eventId, boutId, nextRoundIndex) => {
  const commandResult = await sendAccessCommand("startNextRound", { eventId, boutId, nextRoundIndex });
  if (commandResult !== null) return;
  await updateDoc(doc(db, "events", eventId, "bouts", boutId), {
    currentRound: nextRoundIndex,
  });
};

export const confirmBoutResult = async (eventId, boutId, result) => {
  const commandResult = await sendAccessCommand("confirmBoutResult", { eventId, boutId, result });
  if (commandResult !== null) return;
  await updateDoc(doc(db, "events", eventId, "bouts", boutId), {
    confirmedResult: { ...result, confirmedAt: new Date().toISOString() },
    status: "confirmed",
  });
};
